import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { Button, Field, inputClass } from "../components/kit";

function PasswordInput({ value, onChange, ...props }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input type={show ? "text" : "password"} value={value} onChange={onChange}
        className={`${inputClass} pr-11`} {...props} />
      <button type="button" tabIndex={-1} onClick={() => setShow((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#0F172A] transition-colors"
        aria-label={show ? "Hide password" : "Show password"}>
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function ChangePassword({ forced }) {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated successfully");
      await checkAuth();
      navigate("/company/roles");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#F4F7FB]">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-white p-8 shadow-xl shadow-[#0F172A]/5">
        <div className="w-11 h-11 rounded-xl bg-[#0025D8]/10 flex items-center justify-center mb-5">
          <KeyRound className="w-5 h-5 text-[#0025D8]" />
        </div>
        <h1 className="font-display font-black text-2xl tracking-tight text-[#0F172A] mb-1">
          {forced ? "Set your password" : "Change password"}
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          {forced ? "Choose a secure password to continue." : "Update the password on your account."}
        </p>

        {forced && (
          <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 px-4 py-3 mb-5">
            <p className="font-mono text-[10px] tracking-[0.1em] text-[#B45309] font-bold mb-1">FIRST LOGIN</p>
            <p className="text-sm text-muted-foreground">You must set a new password before continuing. Enter the temporary password from your invite email.</p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <Field label={forced ? "Temporary password" : "Current password"}>
            <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} required />
          </Field>
          <Field label="New password" hint="At least 8 characters.">
            <PasswordInput value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} />
          </Field>
          <Field label="Confirm new password">
            <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </Field>
          <Button type="submit" disabled={saving} loading={saving} className="w-full mt-2">
            {saving ? "Updating…" : "Set new password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
